import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaFolder, FaEdit, FaTrash, FaSave, FaTimes, FaStickyNote } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { notesAPI } from '../services/api';
import './Notes.css';

function Notes() {
  const [notes, setNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general'
  });
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');

  const categories = [
    { value: 'all', label: 'Všetky', color: '#6b7280' },
    { value: 'general', label: 'Všeobecné', color: '#3b82f6' },
    { value: 'work', label: 'Práca', color: '#10b981' },
    { value: 'personal', label: 'Osobné', color: '#8b5cf6' },
    { value: 'ideas', label: 'Nápady', color: '#f59e0b' },
  ];

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    try {
      const response = await notesAPI.getNotes();
      setNotes(response.data);
      if (response.data.length > 0 && !selectedNote) {
        setSelectedNote(response.data[0]);
      }
    } catch (error) {
      toast.error('Chyba pri načítaní poznámok');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await notesAPI.createNote(formData);
      toast.success('Poznámka vytvorená');
      setFormData({ title: '', content: '', category: 'general' });
      setShowAddForm(false);
      fetchNotes();
    } catch (error) {
      toast.error('Chyba pri vytváraní poznámky');
    }
  };

  const handleSaveEdit = async () => {
    try {
      await notesAPI.updateNote(selectedNote.id, selectedNote);
      toast.success('Poznámka uložená');
      setIsEditing(false);
      fetchNotes();
    } catch (error) {
      toast.error('Chyba pri ukladaní poznámky');
    }
  };

  const filteredNotes = categoryFilter === 'all' 
    ? notes 
    : notes.filter(note => note.category === categoryFilter);

  const getCategoryColor = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.color : '#6b7280';
  };

  return (
    <div className="notes-page">
      <div className="notes-sidebar">
        <div className="sidebar-header">
          <h2>Poznámky</h2>
          <motion.button 
            className="add-note-btn"
            onClick={() => setShowAddForm(!showAddForm)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaPlus />
          </motion.button>
        </div>

        <div className="categories">
          {categories.map((cat) => (
            <motion.button
              key={cat.value}
              className={`category-btn ${categoryFilter === cat.value ? 'active' : ''}`}
              onClick={() => setCategoryFilter(cat.value)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ 
                borderLeft: `3px solid ${cat.color}`,
                background: categoryFilter === cat.value ? `${cat.color}10` : 'transparent'
              }}
            >
              <FaFolder style={{ color: cat.color }} />
              <span>{cat.label}</span>
            </motion.button>
          ))}
        </div>

        <div className="notes-list">
          {loading ? (
            <div className="loading-spinner"></div>
          ) : (
            filteredNotes.map((note) => (
              <motion.div
                key={note.id}
                className={`note-item ${selectedNote?.id === note.id ? 'active' : ''}`}
                onClick={() => {
                  setSelectedNote(note);
                  setIsEditing(false);
                }}
                whileHover={{ x: 5 }}
                style={{ borderLeft: `3px solid ${getCategoryColor(note.category)}` }}
              >
                <h4>{note.title}</h4>
                <p>{note.content?.substring(0, 50)}...</p>
                <span className="note-date">
                  {new Date(note.updated_at || note.created_at).toLocaleDateString('sk-SK')}
                </span>
              </motion.div>
            ))
          )}
        </div>
      </div>

      <div className="notes-content">
        <AnimatePresence>
          {showAddForm ? (
            <motion.div 
              className="note-form-container"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <form onSubmit={handleSubmit} className="note-form">
                <input
                  type="text"
                  placeholder="Názov poznámky"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="note-title-input"
                  required
                />
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="note-category-select"
                >
                  <option value="general">Všeobecné</option>
                  <option value="work">Práca</option>
                  <option value="personal">Osobné</option>
                  <option value="ideas">Nápady</option>
                </select>
                <textarea
                  placeholder="Obsah poznámky..."
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  className="note-content-textarea"
                  rows="20"
                />
                <div className="form-buttons">
                  <button type="submit" className="btn btn-primary">
                    <FaSave /> Vytvoriť
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowAddForm(false)}
                  >
                    <FaTimes /> Zrušiť
                  </button>
                </div>
              </form>
            </motion.div>
          ) : selectedNote ? (
            <motion.div 
              className="note-editor"
              key={selectedNote.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="editor-header">
                {isEditing ? (
                  <input
                    type="text"
                    value={selectedNote.title}
                    onChange={(e) => setSelectedNote({...selectedNote, title: e.target.value})}
                    className="note-title-edit"
                  />
                ) : (
                  <h2>{selectedNote.title}</h2>
                )}
                <div className="editor-actions">
                  {isEditing ? (
                    <>
                      <motion.button 
                        className="action-btn save-btn"
                        onClick={handleSaveEdit}
                        whileHover={{ scale: 1.1 }}
                      >
                        <FaSave />
                      </motion.button>
                      <motion.button 
                        className="action-btn cancel-btn"
                        onClick={() => {
                          setIsEditing(false);
                          fetchNotes();
                        }}
                        whileHover={{ scale: 1.1 }}
                      >
                        <FaTimes />
                      </motion.button>
                    </>
                  ) : (
                    <>
                      <motion.button 
                        className="action-btn edit-btn"
                        onClick={() => setIsEditing(true)}
                        whileHover={{ scale: 1.1 }}
                      >
                        <FaEdit />
                      </motion.button>
                      <motion.button 
                        className="action-btn delete-btn"
                        whileHover={{ scale: 1.1 }}
                      >
                        <FaTrash />
                      </motion.button>
                    </>
                  )}
                </div>
              </div>
              <div className="note-meta">
                <span className="category-badge" style={{ background: `${getCategoryColor(selectedNote.category)}20`, color: getCategoryColor(selectedNote.category) }}>
                  {categories.find(c => c.value === selectedNote.category)?.label}
                </span>
                <span className="note-date">
                  Upravené: {new Date(selectedNote.updated_at || selectedNote.created_at).toLocaleString('sk-SK')}
                </span>
              </div>
              {isEditing ? (
                <textarea
                  value={selectedNote.content}
                  onChange={(e) => setSelectedNote({...selectedNote, content: e.target.value})}
                  className="note-content-edit"
                />
              ) : (
                <div className="note-content">
                  {selectedNote.content}
                </div>
              )}
            </motion.div>
          ) : (
            <div className="empty-note">
              <FaStickyNote className="empty-icon" />
              <h3>Vyberte poznámku alebo vytvorte novú</h3>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default Notes;